// @ts-nocheck
import BackButton from '@/components/ui/back-button';
import { useModalAction } from '@/components/ui/modal/modal.context';
import { ThumbsCarousel } from '@/components/ui/thumb-carousel';
import { Routes } from '@/config/routes';
import { displayImage } from '@/lib/display-product-preview-images';
import usePrice from '@/lib/use-price';
import { stickyShortDetailsAtom } from '@/store/sticky-short-details-atom';
import type { ISearchProduct } from '@/types';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { flushSync } from 'react-dom';
import { useEffect, useMemo, useRef } from 'react';
import { useIntersection } from 'react-use';
import ProductCompare from '../widgets/product-compare/product-compare';
import ProductInfos from '../widgets/product-infos/product-infos';
import ProductOutOfStock from '../widgets/product-out-of-stock/product-out-of-stock';
import ProductShipping from '../widgets/product-shipping/product-shipping';
import ProductKits from '../widgets/product-kits/product-kits';
import ProductPBM from '../widgets/product-pbm/product-pbm';
import ProductDiscontinued from '../widgets/product-discontinued';
import ProductAdvertence from '../widgets/product-advertence';
import ProductVariants from './product-variants';
import BuyTogether from '../buy-together/buy-together';
import BadgeGroups from './badge-groups';
import Link from 'next/link';
import { trackCategoryView } from '@/lib/gtm';
import GenericAssociatedDiscount from '../widgets/generic-associated-discount/generic-associated-discount';
import TabsPanel from '@/components/ui/tabs-panel';
import { useProductCharacteristics } from '@/framework/product';
import { showCompareEcomprove } from '@/lib/utils/prices-comparison-check/comparison-check';
import { useProductPromotionalPack } from '../add-to-cart/add-promotional-pack-to-cart/useProductPromotionalPack';
import DynamicSearchProvider from '@/lib/services/typesense/search-providers/dynamic-provider';
import { buildRelatedProductsFilter } from '@/lib/services/typesense/build-filter-by';
import { MIN_PRICE_TO_SHOW_SHIPPING, MIN_DISCOUNT_PERCENTAGE } from '@/framework/utils/constants';
import { DynamicSectionComponents } from '@/types/dynamic-sections';
import { DynamicProductsWrapper } from '../dynamic-products-wrapper';
import { useProgramBenefits } from '@/store/program-benefits-atom';
import {
  CHARACTERISTICS_MAP,
} from '@/lib/constants/characteristics';
import { resolveBulaPdfUrl } from '@/lib/bula-url';
import { useSettings } from '@/framework/settings';
import { creditCardConfig } from '@/lib/utils/creditCard/constants';
import {
  generateInstallmentOptions,
  resolveProductMaxInstallments,
} from '@/lib/utils/creditCard/creditCard';
import {
  findPropertyByName,
  getDetalhesCharacteristicStringValue,
  getProductMaxInstallmentsFromCharacteristicSources,
  shouldShowPBM,
  hasEnoughStockForPromotionalPack,
  promotionalPackHasPayRule,
} from '@/lib/utils/product';
import TagGenericoIcon from '@/components/icons/tag-generico-icon';
import { skipNextModalHistoryCleanup } from '@/components/ui/modal/history-cleanup';

const AddToCart = dynamic(
  () =>
    import('@/components/products/add-to-cart/add-to-cart').then(
      (module) => module.AddToCart,
    ),
  { ssr: false },
);

const ProductPurchaseOptions = dynamic(
  () => import('./product-purchase-options'),
  { ssr: false },
);

const FavoriteButton = dynamic(
  () => import('@/components/products/details/favorite-button'),
  { ssr: false },
);

const ShareButton = dynamic(
  () => import('@/components/products/details/share-button'),
  { ssr: false },
);

function getTabs({ product, priceData, characteristics }) {
  const tabs = [];

  const detalhesCharacteristics = characteristics?.Detalhes?.characteristics;
  const showPBM = shouldShowPBM({
    characteristics: detalhesCharacteristics,
    productPrices: product?.product_prices,
  });

  const laboratoryDiscountFootnote = getDetalhesCharacteristicStringValue(
    characteristics,
    CHARACTERISTICS_MAP.LAB_DISCOUNT_INFO_NOTE.id,
  );

  if (showPBM) {
    tabs.push({
      name: 'Ativar beneficio Lab.',
      slug: 'pbm',
      content: (
        <ProductPBM
          key={product.sku}
          product={product}
          laboratoryDiscountFootnote={laboratoryDiscountFootnote}
        />
      ),
      tabColor: 'bg-[#E8F2FB]',
      tabTextColor: 'text-accent',
    });
  }

  const payValue = promotionalPackHasPayRule(product.characteristics);

  if (
    showCompareEcomprove({
      product_prices: product.product_prices,
      current_product_price: product.price,
      current_product_sale_price: product.sale_price,
    })
  ) {
    tabs.push({
      name: 'Compare e Comprove',
      slug: 'compare-e-comprove',
      content: <ProductCompare products={[{ ...product, ...priceData }]} />,
      tabColor: 'bg-[rgba(240,86,122,0.1)]',
      tabTextColor: 'text-red-500',
    });
  }

  if (product.is_promotional_pack && hasEnoughStockForPromotionalPack(product)) {
    tabs.push({
      name: 'Leve mais por menos',
      slug: 'leve-mais-por-menos',
      content: <ProductKits product={product} />,
      tabColor: payValue ? 'bg-amber-200' : 'bg-[#e1ebf4]',
      tabTextColor: 'text-accent text-blue-800',
    });
  }

  return tabs;
}

type Props = {
  product: ISearchProduct;
  backBtn?: boolean;
  isModal?: boolean;
  selectedTab?: string;
};
const Details: React.FC<Props> = ({
  product,
  backBtn = true,
  isModal = false,
  selectedTab,
}) => {
  const {
    product_id,
    name,
    image, //could only had image we need to think it also
    // description,
    // unit,
    gallery,
    // type,
    quantity,
    in_stock,
    slug,
    sku,
    brand,
    manufacturer,
    // video,
  } = product ?? {};
  const video = [];
  const relatedProductsFilter = buildRelatedProductsFilter({
    brand,
    manufacturer,
  });
  const { settings } = useSettings();
  const { promotional_pack_details } = useProductPromotionalPack(product);

  const [_, setShowStickyShortDetails] = useAtom(stickyShortDetailsAtom);

  const priceData = {
    prices: product.product_prices,
    price: product.price,
    sale_price: product.sale_price,
    discount: product?.discount || 0,
  };

  const { data: characteristics } = useProductCharacteristics({
    id: product.product_id,
  });

  const Detalhes = characteristics?.Detalhes?.characteristics;

  const showPbmOnPage = shouldShowPBM({
    characteristics: Detalhes,
    productPrices: product?.product_prices,
  });

  const router = useRouter();
  const { closeModal } = useModalAction();
  const intersectionRef = useRef(null);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });
  useEffect(() => {
    if (intersection && intersection.isIntersecting) {
      setShowStickyShortDetails(false);
      return;
    }
    if (intersection && !intersection.isIntersecting) {
      setShowStickyShortDetails(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intersection]);

  const { programBenefits } = useProgramBenefits();
  const benefit = programBenefits[product.id];

  // If PBM benefit is active for this product, override price and set cart quantity to 1
  let effectiveQuantity = 1;
  if (!benefit) {
    effectiveQuantity = 1;
  }
  const effectiveOriginalPrice = product.price;
  const effectiveSalePrice = benefit ? benefit.price : priceData?.sale_price ?? priceData?.price;

  const { price, basePrice, discount, discountValue } = usePrice({
    amount: effectiveSalePrice * effectiveQuantity,
    baseAmount: effectiveOriginalPrice * effectiveQuantity,
  });

  // Only show discount if it's greater than or equal to the minimum threshold
  const shouldShowDiscount = discountValue !== null && discountValue >= MIN_DISCOUNT_PERCENTAGE;

  const navigate = (path: string) => {
    if (isModal) {
      skipNextModalHistoryCleanup();
      flushSync(() => {
        closeModal();
      });
      // replace the modal overlay history entry so one back click returns to search
      void router.replace(path);
    } else {
      void router.push(path);
    }
  };
  let selectedVariation: any = {};
  // if (isSelected) {
  //   selectedVariation = product?.variation_options?.find((o: any) =>
  //     isEqual(
  //       o.options.map((v: any) => v.value).sort(),
  //       Object.values(attributes).sort(),
  //     ),
  //   );
  // }

  const previewImages = displayImage(selectedVariation?.image, gallery, image);

  const isOutOfStock = Number(quantity) <= 0;
const marca = findPropertyByName(Detalhes, 'Marca');
  const fabricante = findPropertyByName(Detalhes, 'Fabricante');
  const categories = (product as any)?.categories ?? [];

  const discontinuedCharacteristic =
    characteristics?.Detalhes?.characteristics?.find(
      (char: any) => char.id === CHARACTERISTICS_MAP.DESCONTINUADO.id,
    );
  const discontinuedValue = discontinuedCharacteristic?.value || null;

  const advertenceCharacteristic =
    characteristics?.Detalhes?.characteristics?.find(
      (char: any) => char.id === CHARACTERISTICS_MAP.ADVERTENCE.id,
    ) ?? product?.characteristics?.find((c: any) => c?.id === CHARACTERISTICS_MAP.ADVERTENCE.id);
  const advertences = useMemo(() => {
    const value = advertenceCharacteristic?.valueJson;
    if (Array.isArray(value)) {
      return value;
    }
    return [];
  }, [advertenceCharacteristic]);

  const referenceProduct = useMemo(
    () => Detalhes?.find((char: any) => char.id === CHARACTERISTICS_MAP.REFERENCE.id)?.value || 
           product?.characteristics?.find((item) => item?.id === CHARACTERISTICS_MAP.REFERENCE.id)?.value || null,
    [Detalhes, product],
  );


  const bulaCharacteristic = useMemo(() => {
    const bulaChar = Detalhes?.find((char: any) => char.id === CHARACTERISTICS_MAP.BULA.id) ||
                     product?.characteristics?.find((item) => item?.id === CHARACTERISTICS_MAP.BULA.id);
    
    const pdfUrl = resolveBulaPdfUrl(bulaChar?.valueJson);
    if (!pdfUrl) return null;

    return {
      pdfUrl,
    };
  }, [Detalhes, product]);

  // Calculate maximum installment option for the product
  const maxInstallment = useMemo(() => {
    const installmentRules =
      settings?.installmentRules ||
      creditCardConfig.creditCard.installmentRules;
    const productMaxInstallments =
      getProductMaxInstallmentsFromCharacteristicSources(
        product.characteristics,
        Detalhes,
      );
    const maxInstallmentsCap = resolveProductMaxInstallments(
      product.sale_price,
      installmentRules,
      productMaxInstallments,
    );
    const options = generateInstallmentOptions(
      product.sale_price,
      installmentRules,
      maxInstallmentsCap,
    );

    return options[options.length - 1];
  }, [
    product.sale_price,
    product.characteristics,
    Detalhes,
    settings?.installmentRules,
  ]);

  return (
    <article className="rounded-lg">
      {/* Infos render once: below the gallery on desktop (grid row 2), after the purchase column on mobile (DOM order). */}
      <div className="flex flex-col md:grid md:grid-cols-2 md:grid-rows-[auto_1fr]">
        <div className="overflow-visible p-6 pt-10 md:col-start-1 md:row-start-1 md:pb-0 lg:p-14 lg:pb-0 xl:p-16 xl:pb-0">
          <div className="mb-8 flex items-center justify-between lg:mb-10">
            {backBtn && <BackButton />}
            {shouldShowDiscount && discount && (
              <div className="rounded-full bg-yellow-500 px-3 text-xs font-semibold leading-6 text-black ltr:ml-auto rtl:mr-auto">
                {discount}
              </div>
            )}
          </div>

          <div className="product-gallery relative">
            {referenceProduct ? (
              <div
                className={classNames(
                  'absolute z-10 flex items-center gap-2',
                  {
                    'top-3 md:top-4 left-3 md:left-28': !isModal,
                    'left-0 md:left-4 -top-14 md:-top-16': isModal,
                  },
                )}
              >
                <TagGenericoIcon
                  className="w-8 h-8 md:w-8 md:h-8"
                />
                <div className="text-left">
                  <p
                    className={classNames('font-bold text-black leading-tight text-[10px] md:text-xs')}
                  >
                    Genérico do:
                  </p>
                  <p
                    className={classNames('font-bold text-blue-600 leading-tight text-[10px] md:text-xs')}
                  >
                    {referenceProduct}
                  </p>
                </div>
              </div>
            ) : null}
            <ThumbsCarousel
              gallery={previewImages}
              video={video}
              hideThumbs={
                previewImages.length && video?.length
                  ? false
                  : previewImages.length <= 1 && !bulaCharacteristic
              }
              isModal={isModal}
              bula={bulaCharacteristic}
              productName={name}
            />
          </div>

          <div
            className={classNames(
              'relative z-10 mt-6 hidden w-full md:block',
              isModal ? 'overflow-hidden' : 'overflow-visible',
            )}
          >
            <BuyTogether
              mainProduct={product}
              mainSalePrice={effectiveSalePrice}
              variant="desktop"
              isModal={isModal}
              onAssociatedProductNavigate={isModal ? navigate : undefined}
            />
          </div>
        </div>

        <div className="flex flex-col items-start p-5 pt-4 pb-0 md:col-start-2 md:row-span-2 md:row-start-1 md:pb-20 lg:p-14 xl:p-16">
          <div className="w-full" ref={intersectionRef}>
            <div className="flex w-full items-start justify-between space-x-8 rtl:space-x-reverse">
              <h1
                className={classNames(
                  `text-lg tracking-tight text-accent font-bold md:text-xl xl:text-2xl`,
                  {
                    'cursor-pointer transition-colors hover:text-accent':
                      isModal,
                  },
                )}
                {...(isModal && {
                  onClick: () => navigate(Routes.product(slug, `${sku}`)),
                })}
              >
                {name}
              </h1>

              <div className="flex items-center gap-2">
                <FavoriteButton
                  productId={product_id}
                  className={classNames({ 'mr-1': isModal })}
                />
                <ShareButton
                  productId={product_id}
                  slug={slug}
                  sku={sku}
                  name={name}
                />
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="block text-sm font-normal text-body">
                {marca} - {fabricante}
              </span>
            </div>

            {Array.isArray(categories) && categories.length > 0 && (
              <div className="mt-1">
                <BadgeGroups>
                  {categories.map((category: { id: number; name: string; slug: string }) => (
                    <Link
                      key={category.id}
                      href={Routes.category(category.slug)}
                      className="bg-transparent text-md text-body transition-colors after:content-[','] last:after:content-[''] hover:text-accent focus:bg-opacity-100 focus:outline-none ltr:pr-1.5 ltr:last:pr-0 rtl:pl-0.5 rtl:last:pl-0"
                      onClick={() => {
                        trackCategoryView(category.name);
                        if (isModal) closeModal();
                      }}
                    >
                      {category.name}
                    </Link>
                  ))}
                </BadgeGroups>
              </div>
            )}

            <GenericAssociatedDiscount
              product={product}
              associationType="generico"
              className="mt-4"
            />

            <ProductVariants product={product} isModal={isModal} />

            {!isOutOfStock && (() => {
              const tabs = getTabs({
                product: promotional_pack_details.product,
                priceData,
                characteristics,
              })
                .filter((t) => !(showPbmOnPage && t.slug === 'pbm'))
                .slice(0, 2);
              return (
                <>
                  {showPbmOnPage && (
                    <ProductPurchaseOptions
                      product={product}
                      characteristics={characteristics}
                      className="mt-4"
                    />
                  )}
                  {tabs.length > 0 ? (
                    <TabsPanel defaultTab={selectedTab} tabs={tabs} />
                  ) : null}
                </>
              );
            })()}

            {isOutOfStock && <ProductOutOfStock productId={product_id} />}

            {!showPbmOnPage && (
              <div className="mt-6 flex flex-col items-center w-full gap-4 md:mt-6 lg:flex-row">
                <span className="flex items-start flex-col w-full">
                  {basePrice && shouldShowDiscount && (
                    <del className="text-sm font-normal text-muted md:text-base">
                      De: {basePrice}
                    </del>
                  )}
                  <ins className="break-words text-2xl text-dark-blue no-underline md:whitespace-nowrap md:text-3xl">
                    Por: <span className="font-semibold text-3xl">{price}</span>
                  </ins>
                  {maxInstallment && (
                    <span className="block text-accent text-[12px] mt-[4px] leading-3 lg:text-[14px]">
                      ou {maxInstallment.label}
                    </span>
                  )}
                </span>
                {Number(quantity) > 0 ? (
                  <div className="mb-3 w-full lg:mb-0 lg:max-w-[400px]">
                    <AddToCart data={{
                      ...product,
                      price: effectiveOriginalPrice,
                      sale_price: effectiveSalePrice,
                      pbm_flag: !!benefit,
                      pbm_sale_price_id: benefit?.salePriceId,
                      pbm_cpf: benefit?.cpf,
                      pbm_quantity: benefit?.quantity,
                      pbm_program: benefit?.program,
                      pbm_promotion: benefit?.promotion,
                    }} variant="big" />
                  </div>
                ) : (
                  ''
                )}
              </div>
            )}

            <div className="mt-4 w-full md:hidden">
              <BuyTogether
                mainProduct={product}
                mainSalePrice={effectiveSalePrice}
                variant="mobile"
                isModal={isModal}
                onAssociatedProductNavigate={isModal ? navigate : undefined}
              />
            </div>
          </div>

          <ProductDiscontinued discontinuedValue={discontinuedValue} />

          <ProductAdvertence advertences={advertences} />

          {priceData?.sale_price >= MIN_PRICE_TO_SHOW_SHIPPING && (
            <ProductShipping product={product} />
          )}
        </div>

        <div className="w-full px-5 pt-6 pb-20 md:col-start-1 md:row-start-2 md:px-6 md:pt-0 md:pb-6 lg:px-14 lg:pb-14 xl:px-16 xl:pb-0">
          <ProductInfos product={product} characteristics={characteristics} />
        </div>
      </div>

      {/* Show Standout only on Product Page */}
      {!isModal && (
        <div className="px-6 lg:px-14 xl:px-16">
          <div id="syndi_inline"></div>
        </div>
      )}

      <div className="my-8 border-b border-border-200 border-opacity-70"></div>

      <div className="px-6 lg:px-14 xl:px-16">
        <DynamicSearchProvider
          key={`product-details-slider-${product_id}`}
          filters={relatedProductsFilter}
          items_per_page={10}
        >
          <DynamicProductsWrapper
            component={DynamicSectionComponents.PRODUCTS_SLIDER}
            uniqueKey={`product-details-slider-${product_id}`}
          />
        </DynamicSearchProvider>
      </div>
    </article>
  );
};

export default Details;
